import React, { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import FsLightbox from "fslightbox-react";
import {
  FiUpload,
  FiImage,
  FiVideo,
  FiFileText,
  FiFile,
  FiX,
  FiEye,
} from "react-icons/fi";

function ReportDropZone({
  type,
  uploadFile,
  removeFile,  // Add this new prop
  disabledPlaceHolder = false,
  clearDropZone = false,
}) {
  const [accept, setAccept] = useState("");
  const [files, setFiles] = useState([]);
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1,
  });
  const { t } = useTranslation(["workpermit", "util"]);
  const previewUrlsRef = useRef([]);

  useEffect(() => {
    if (type == 1) {
      setAccept(".jpg, .jpeg, .png");
    } else if (type == 2) {
      setAccept(".mp4, .mkv, .wav, .omv");
    } else if (type == 3 || type == 4 || type == 7) {
      setAccept(".pdf");
    } else if (type == 5) {
      setAccept(".jpg, .jpeg, .png, .mp4, .mkv, .wav, .omv");
    } else if (type == 6) {
      setAccept(".pdf, .doc, .docx, .xls, .xlsx");
    } else if (type == 8) {
      setAccept(
        ".jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .mp4, .mkv, .wav, .omv, .jpg, .jpeg, .png, .mp4, .mkv, .wav, .omv "
      );
    } else if (type == 9) {
      setAccept(".jpg, .jpeg, .png, .pdf");
    } else if (type == 10) {
      setAccept(".doc, .docx, .xls, .xlsx, .jpg, .jpeg, .png, .pdf ");
    }
  }, [type]);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    accept,
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => {
        const previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.push(previewUrl);
        return {
          file,
          preview: previewUrl,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          type: file.type,
        };
      });

      const updatedFiles = [...files, ...newFiles];
      setFiles(updatedFiles);
      acceptedFiles.forEach((file) => uploadFile(file));
    },
  });

  const handleRemoveFile = (fileId) => {
    const fileToRemove = files.find((f) => f.id === fileId);
    if (fileToRemove) {
      // Call the parent's removeFile callback if provided
      if (removeFile && typeof removeFile === 'function') {
        removeFile(fileToRemove.file);
      }
      
      URL.revokeObjectURL(fileToRemove.preview);
      const index = previewUrlsRef.current.indexOf(fileToRemove.preview);
      if (index > -1) {
        previewUrlsRef.current.splice(index, 1);
      }
    }
    setFiles(files.filter((f) => f.id !== fileId));
  };

  const openLightbox = (slideIndex) => {
    if (slideIndex >= 0 && slideIndex < lightboxSources.length) {
      setLightboxController({
        toggler: !lightboxController.toggler,
        slide: slideIndex + 1,
      });
    }
  };

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (clearDropZone && files.length > 0) {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
      previewUrlsRef.current = [];
      setFiles([]);
    }
  }, [clearDropZone]);

  const getFileIcon = (fileType) => {
    if (fileType?.includes("image")) {
      return <FiImage size={18} />;
    } else if (fileType?.includes("video")) {
      return <FiVideo size={18} />;
    } else if (fileType?.includes("pdf")) {
      return <FiFileText size={18} />;
    }
    return <FiFile size={18} />;
  };

  const previewableFiles = files.filter(
    (file) => file.type?.includes("image") || file.type?.includes("video")
  );

  const lightboxSources = previewableFiles.map((file) => file.preview);

  // For file list display (similar to original files1)
  const fileListItems = files.map((fileData) =>
    type == 3 || type == 4 || type == 7 || type == 6 || type == 8 ? (
      <FileListItem key={fileData.id}>{fileData.file.name}</FileListItem>
    ) : null
  );

  // For file rejection items (similar to original fileRejectionItems)
  const fileRejectionItems = fileRejections.map(({ file, errors }) =>
    errors.map((e) => (
      <li
        key={`${file.name}-${e.code}`}
        style={{ margin: "8px 0", color: "red" }}
      >
        {e.message}
      </li>
    ))
  );

  return (
    <Container>
      <DropzoneArea {...getRootProps()}>
        <input {...getInputProps()} />
        <UploadIcon>
          <FiUpload size={24} />
        </UploadIcon>
        <UploadText>{t("dragDropFileHere")}</UploadText>
        {!disabledPlaceHolder && (
          <AcceptText>
            {/* EXACT SAME TRANSLATION MAPPING AS ORIGINAL */}
            {type == 1 ? (
              <em className="clide-v2-fs-6 clide-v2-primary-color">
                ({t("typeOneAccepted")})
              </em>
            ) : null}
            {type == 2 ? (
              <em className="clide-v2-fs-6 clide-v2-primary-color">
                ({t("typeTwoAccepted")})
              </em>
            ) : null}
            {type == 3 || type == 4 || type == 7 ? (
              <em className="clide-v2-fs-6 clide-v2-primary-color">
                ({t("typeThreeAccepted")})
              </em>
            ) : null}
            {type == 5 ? (
              <em className="clide-v2-fs-6 clide-v2-primary-color">
                ({t("typefiveAccepted")})
              </em>
            ) : null}
            {type == 6 ? (
              <em className="clide-v2-fs-6 clide-v2-primary-color">
                ({t("typeSixAccepted")})
              </em>
            ) : null}
            {type == 10 ? (
              <em className="clide-v2-fs-6 clide-v2-primary-color">
                ({t("typeTenAccepted")})
              </em>
            ) : null}
          </AcceptText>
        )}
        <HintText>Click or drag files here</HintText>
      </DropzoneArea>

      {/* File rejection errors */}
      {fileRejections.length > 0 && (
        <ErrorContainer>
          <ul
            style={{
              textAlign: "start",
              color: "red",
              margin: 0,
              paddingLeft: "20px",
            }}
          >
            {fileRejectionItems}
          </ul>
        </ErrorContainer>
      )}

      {/* File list for document types */}
      {(type == 3 || type == 4 || type == 7 || type == 6 || type == 8) &&
        files.length > 0 && (
          <FilesListContainer>
            <ul style={{ textAlign: "start", margin: 0, paddingLeft: "20px" }}>
              {fileListItems}
            </ul>
          </FilesListContainer>
        )}

      {/* Uploaded files preview */}
      {files.length > 0 && (
        <FilesContainer>
          <FilesHeader>
            <FilesCount>Files ({files.length})</FilesCount>
          </FilesHeader>
          <FilesGrid>
            {files.map((fileData, index) => {
              const isPreviewable =
                fileData.type?.includes("image") ||
                fileData.type?.includes("video");

              const previewableIndex = previewableFiles.findIndex(
                (previewableFile) => previewableFile.id === fileData.id
              );

              return (
                <FileCard key={fileData.id}>
                  <FileHeader>
                    <FileIcon>{getFileIcon(fileData.type)}</FileIcon>
                    <FileName title={fileData.file.name}>
                      {fileData.file.name.length > 15
                        ? fileData.file.name.substring(0, 12) +
                          "..." +
                          fileData.file.name.substring(
                            fileData.file.name.lastIndexOf(".")
                          )
                        : fileData.file.name}
                    </FileName>
                    <RemoveButton onClick={() => handleRemoveFile(fileData.id)}>
                      <FiX size={16} />
                    </RemoveButton>
                  </FileHeader>

                  <FilePreview
                    onClick={() =>
                      isPreviewable &&
                      previewableIndex >= 0 &&
                      openLightbox(previewableIndex)
                    }
                    previewable={isPreviewable && previewableIndex >= 0}
                  >
                    {fileData.type?.includes("image") ? (
                      <ImagePreview src={fileData.preview} alt="preview" />
                    ) : fileData.type?.includes("video") ? (
                      <VideoPreview>
                        <FiVideo size={24} />
                        <PlayOverlay>
                          <FiEye size={16} />
                        </PlayOverlay>
                      </VideoPreview>
                    ) : (
                      <DocumentPreview>
                        {getFileIcon(fileData.type)}
                        <FileSize>
                          {(fileData.file.size / 1024).toFixed(0)}KB
                        </FileSize>
                      </DocumentPreview>
                    )}
                  </FilePreview>

                  <FileFooter>
                    {isPreviewable && previewableIndex >= 0 && (
                      <PreviewButton
                        onClick={() => openLightbox(previewableIndex)}
                      >
                        <FiEye size={14} />
                        <span>Preview</span>
                      </PreviewButton>
                    )}
                    <FileSizeText>
                      {(fileData.file.size / 1024).toFixed(0)}KB
                    </FileSizeText>
                  </FileFooter>
                </FileCard>
              );
            })}
          </FilesGrid>
        </FilesContainer>
      )}

      {lightboxSources.length > 0 && (
        <FsLightbox
          toggler={lightboxController.toggler}
          sources={lightboxSources}
          slide={lightboxController.slide}
          key={lightboxSources.length}
        />
      )}
    </Container>
  );
}

// Styled Components remain the same...
const Container = styled.div`
  width: 100%;
`;

const DropzoneArea = styled.div`
  border: 2px dashed #ff6347;
  border-radius: 8px;
  padding: 16px 16px;
  text-align: center;
  background-color: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #ff6347;
    background-color: rgba(252, 219, 213, 1);
  }
`;

const UploadIcon = styled.div`
  color: #ff6347;
  margin-bottom: 12px;
`;

const UploadText = styled.p`
  color: #374151;
  font-weight: 500;
  margin-bottom: 4px;
  font-size: 14px;
`;

const AcceptText = styled.div`
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 8px;

  .clide-v2-fs-6 {
    font-size: 0.875rem;
  }

  .clide-v2-primary-color {
    color: #ff6347;
  }

  em {
    font-style: italic;
  }
`;

const HintText = styled.p`
  color: #9ca3af;
  font-size: 11px;
`;

const ErrorContainer = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  background-color: #fef2f2;
  border-radius: 6px;
  border: 1px solid #fecaca;
`;

const FilesListContainer = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const FileListItem = styled.li`
  color: #374151;
  font-size: 12px;
  margin: 4px 0;
  list-style-type: disc;
`;

const FilesContainer = styled.div`
  margin-top: 16px;
`;

const FilesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const FilesCount = styled.h6`
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`;

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
`;

const FileCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: rgba(252, 219, 213, 1);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  }
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 6px;
`;

const FileIcon = styled.div`
  color: #6b7280;
  flex-shrink: 0;
`;

const FileName = styled.div`
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  color: #9ca3af;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: #dc2626;
    background-color: #fef2f2;
  }
`;

const FilePreview = styled.div`
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f3f4f6;
  margin-bottom: 8px;
  position: relative;
  cursor: ${(props) => (props.previewable ? "pointer" : "default")};

  ${(props) =>
    props.previewable &&
    `
    &:hover {
      opacity: 0.9;
    }
  `}
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  position: relative;
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.6);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${VideoPreview}:hover & {
    opacity: 1;
  }
`;

const DocumentPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  gap: 6px;
`;

const FileFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #3b82f6;
  background: none;
  border: none;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #eff6ff;
  }

  span {
    margin-top: 1px;
  }
`;

const FileSizeText = styled.span`
  color: #9ca3af;
  font-size: 11px;
  font-weight: 500;
`;

const FileSize = styled.span`
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
`;

export default ReportDropZone;